/**
 * `aliasData.ts` — part name aliases: alternate, language-tagged names for a
 * snapshot (root or part).
 *
 * **This has no Unity counterpart.** It is a web-first feature, so there is no
 * `Models/Data/*.cs` to mirror and no existing server contract to preserve —
 * which makes the storage shape a real decision rather than a transcription.
 * The choices, and why:
 *
 *  - **Dynamo `L` of `M{Language, Value}`, not a JSON string.** The one
 *    existing precedent for a structured sub-object on a row is
 *    `PersistentSpinJson`, which is an opaque string — but only because Unity
 *    writes it with `JsonUtility` and the web has to round-trip those exact
 *    bytes (`persistentSpinData.ts`). Nothing here is round-tripping a foreign
 *    serializer, and every other structured attribute Campfire writes is typed
 *    (`Position` is an `M`, `MaterialIDs` an `L` of `S`). A typed list keeps
 *    aliases readable by a future Unity port and by anything querying Dynamo
 *    directly, and `marshall`/`unmarshall` handle it natively in both
 *    directions.
 *  - **The same `Aliases` attribute on BOTH rows it lives on.** The per-project
 *    copy sits on the entity-snapshot row; the reusable library copy sits on
 *    the asset's part row (written by Save to Library). Identical shape makes
 *    the bake a copy and the out-of-sync comparison a direct compare, instead
 *    of two mappers that can drift.
 *  - **Omitted when empty**, matching Unity's `SafeSetListOfStringsAttribute`
 *    convention used by `MaterialIDs` on both rows.
 *
 * Reads are deliberately tolerant: a row written by a future client with extra
 * keys, or with a missing `Language`, still yields usable aliases rather than
 * throwing mid scene-load.
 */

/** A single alternate name for a snapshot / part. */
export interface PartAlias {
  /** ISO 639-1 code, lower-cased (see {@link ALIAS_LANGUAGES}). */
  readonly language: string;
  readonly value: string;
}

/** Wire/Dynamo shape of one alias entry (`M` inside the `Aliases` list). */
export interface AliasDto {
  readonly Language?: string;
  readonly Value?: string;
  readonly [extra: string]: unknown;
}

/**
 * The known alias languages: the codes this client recognises and their labels.
 *
 * There is no language concept anywhere else in Campfire — not in the web port
 * and not in the Unity sources (no `SystemLanguage` use, no locale table) — so
 * this list is introduced here rather than ported. It is a curated ISO 639-1
 * set rather than the full 180-odd codes: these cover the languages Campfire's
 * industrial documentation actually ships in. Extend the list rather than
 * letting call sites invent codes.
 *
 * **Nothing currently offers these for selection.** The alias editor's language
 * picker was dropped on 2026-08-07 by direction, so every alias authored HERE
 * is {@link DEFAULT_ALIAS_LANGUAGE}. The table stays because the field itself is
 * live — rows arrive from other clients, ride the Save-to-Library bake and the
 * library drift merge, and print into Spark's scene digest — and because
 * {@link aliasLanguageLabel} is the one place a stored code becomes readable if
 * a surface ever needs to show one. Re-adding a picker means reading this list,
 * not re-deriving it.
 */
export const ALIAS_LANGUAGES: ReadonlyArray<{ readonly code: string; readonly label: string }> = [
  { code: "en", label: "English" },
  { code: "de", label: "German" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "nl", label: "Dutch" },
  { code: "pl", label: "Polish" },
  { code: "cs", label: "Czech" },
  { code: "sv", label: "Swedish" },
  { code: "da", label: "Danish" },
  { code: "fi", label: "Finnish" },
  { code: "no", label: "Norwegian" },
  { code: "tr", label: "Turkish" },
  { code: "ru", label: "Russian" },
  { code: "uk", label: "Ukrainian" },
  { code: "ar", label: "Arabic" },
  { code: "he", label: "Hebrew" },
  { code: "hi", label: "Hindi" },
  { code: "zh", label: "Chinese" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
];

/** Default language for a newly added alias row. */
export const DEFAULT_ALIAS_LANGUAGE = "en";

const K_LANGUAGE_LABELS: ReadonlyMap<string, string> = new Map(
  ALIAS_LANGUAGES.map((l) => [l.code, l.label] as const),
);

/**
 * Human label for a code. An UNKNOWN code returns the code itself rather than
 * a placeholder: a row written by another client (or a future longer list)
 * must stay legible instead of rendering as "Unknown".
 */
export function aliasLanguageLabel(code: string): string {
  return K_LANGUAGE_LABELS.get(code) ?? code;
}

/**
 * Read the `Aliases` attribute off an unmarshalled Dynamo row.
 *
 * Entries with a blank `Value` are dropped — an alias with no text is not a
 * name, and letting one through would make the "+N aliases" count lie. A blank
 * `Language` falls back to {@link DEFAULT_ALIAS_LANGUAGE} rather than dropping
 * the entry, because the value is the part the user actually typed.
 */
export function readAliases(raw: unknown): ReadonlyArray<PartAlias> {
  if (!Array.isArray(raw)) return [];
  const out: PartAlias[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const dto = entry as AliasDto;
    const value = typeof dto.Value === "string" ? dto.Value.trim() : "";
    if (!value) continue;
    const language =
      typeof dto.Language === "string" && dto.Language.trim()
        ? dto.Language.trim().toLowerCase()
        : DEFAULT_ALIAS_LANGUAGE;
    out.push({ language, value });
  }
  return out;
}

/** Marshalling shape for a write. Pairs with {@link readAliases}. */
export function aliasesToDto(aliases: ReadonlyArray<PartAlias>): AliasDto[] {
  return normalizeAliases(aliases).map((a) => ({ Language: a.language, Value: a.value }));
}

/**
 * Trim, drop blank values, lower-case codes, and collapse exact duplicates
 * while preserving the author's order. Run this before persisting so the
 * stored list is canonical — {@link aliasesEqual} and the library-sync
 * comparison both assume it.
 */
export function normalizeAliases(
  aliases: ReadonlyArray<PartAlias>,
): ReadonlyArray<PartAlias> {
  const seen = new Set<string>();
  const out: PartAlias[] = [];
  for (const alias of aliases) {
    const value = (alias?.value ?? "").trim();
    if (!value) continue;
    const language = ((alias?.language ?? "").trim() || DEFAULT_ALIAS_LANGUAGE).toLowerCase();
    const key = `${language} ${value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ language, value });
  }
  return out;
}

/**
 * Set-equality over two alias lists.
 *
 * Deliberately ORDER-INSENSITIVE. Its main caller is the library out-of-sync
 * check, and the alias list carries no primary/secondary meaning — so a
 * reordered-but-identical list is the same set of names, and treating it as a
 * difference would raise an "update available" toast that changes nothing when
 * accepted.
 */
export function aliasesEqual(
  a: ReadonlyArray<PartAlias>,
  b: ReadonlyArray<PartAlias>,
): boolean {
  const left = normalizeAliases(a);
  const right = normalizeAliases(b);
  if (left.length !== right.length) return false;
  const key = (alias: PartAlias) => `${alias.language} ${alias.value}`;
  const bag = new Set(left.map(key));
  return right.every((alias) => bag.has(key(alias)));
}

/**
 * Entries the LIBRARY has that the local copy does not.
 *
 * This is the drift test, and it is deliberately DIRECTIONAL rather than
 * `!aliasesEqual(...)`. A symmetric compare also fires when the local copy is
 * merely AHEAD — the user added a language in this project and never saved it
 * — which would announce "changed in the Library" about a library that never
 * moved, and offer an Update whose only effect is to delete their work. Only a
 * library-side entry the project is missing is something to adopt.
 */
export function libraryAliasAdditions(
  local: ReadonlyArray<PartAlias>,
  library: ReadonlyArray<PartAlias>,
): ReadonlyArray<PartAlias> {
  const have = new Set(normalizeAliases(local).map((a) => `${a.language} ${a.value}`));
  return normalizeAliases(library).filter((a) => !have.has(`${a.language} ${a.value}`));
}

/**
 * Adopt the library's aliases without destroying local-only work.
 *
 * The library is authoritative for every LANGUAGE it defines — so a renamed
 * entry replaces rather than accumulating a stale twin alongside it — while a
 * language only this project uses survives untouched. A whole-list replace
 * would be simpler and is what "Update" naively suggests, but it silently
 * deletes the local additions the directional gate above went to the trouble
 * of not complaining about.
 *
 * The one lossy case, stated so it is a choice and not an accident: a second
 * alias added locally in a language the library ALSO defines is dropped, since
 * that language belongs to the library.
 */
export function mergeLibraryAliases(
  local: ReadonlyArray<PartAlias>,
  library: ReadonlyArray<PartAlias>,
): ReadonlyArray<PartAlias> {
  const libraryAliases = normalizeAliases(library);
  const claimed = new Set(libraryAliases.map((a) => a.language));
  const localOnly = normalizeAliases(local).filter((a) => !claimed.has(a.language));
  return normalizeAliases([...libraryAliases, ...localOnly]);
}

/**
 * Label for the inspector's alias affordance.
 *
 * Shows the aliases THEMSELVES, as many as fit, and folds the rest into a
 * count: `aka Robot Dog, Robot, Dog... + 4 aliases`. A bare count told the user
 * only that aliases exist, which is the least interesting fact about them —
 * the names are what identify the part. The `aka ` prefix says what the list IS;
 * without it a run of names under the name field reads as another field's value.
 *
 * `fits` is the caller's measurement of whether a candidate label fits the
 * available width; it lives at the call site because only the view knows the
 * button's font and box, and it keeps this function pure and testable.
 *
 * Fitting is greedy from the widest candidate down, and both the PREFIX and the
 * SUFFIX are included in every measurement — adding either after choosing how
 * many names to show is what makes this kind of label overflow by exactly the
 * width of the part that was measured last.
 */
export function formatAliasButtonLabel(
  aliases: ReadonlyArray<PartAlias>,
  fits: (label: string) => boolean = () => true,
): string {
  const values = normalizeAliases(aliases).map((a) => a.value);
  if (values.length === 0) return "add alias";

  for (let shown = values.length; shown >= 1; shown--) {
    const hidden = values.length - shown;
    const list = shown === values.length
      ? values.join(", ")
      : `${values.slice(0, shown).join(", ")}... + ${countLabel(hidden)}`;
    const label = `${K_ALIAS_LABEL_PREFIX}${list}`;
    if (fits(label)) return label;
  }
  // Not even one name fits — fall back to the count alone rather than emitting
  // a label the button will visibly clip mid-word. Unprefixed on purpose: this
  // is no longer a list of names, so "aka" would be describing nothing.
  return countLabel(values.length);
}

/** Names what the list is, so it cannot be read as another field's value. */
const K_ALIAS_LABEL_PREFIX = "aka ";

function countLabel(count: number): string {
  return count === 1 ? "1 alias" : `${count} aliases`;
}
